import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Tabs,
  Tab,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaEye,
  FaInfoCircle,
  FaRedo,
  FaSearch,
  FaFilter,
  FaClock,
  FaCheckCircle,
  FaBan,
  FaDownload,
  FaFile,
} from "react-icons/fa";

const ComplaintsManagement = () => {
  const [openComplaints, setOpenComplaints] = useState([]);
  const [inProgressComplaints, setInProgressComplaints] = useState([]);
  const [resolvedComplaints, setResolvedComplaints] = useState([]);
  const [rejectedComplaints, setRejectedComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [activeTab, setActiveTab] = useState("open");
  const [counts, setCounts] = useState({
    open: 0,
    inProgress: 0,
    resolved: 0,
    rejected: 0,
  });

  // Download state: tracks which complaint IDs are currently downloading
  const [downloadingLogs, setDownloadingLogs] = useState(new Set());

  // Update complaint form state
  const [updateStatus, setUpdateStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterPlatform, setFilterPlatform] = useState("all");

  useEffect(() => {
    fetchAllComplaints();
  }, []);

  const getAdminToken = () =>
    localStorage.getItem("adminToken") ||
    sessionStorage.getItem("adminToken");

  const getApiBase = () => process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

  const fetchAllComplaints = async () => {
    try {
      setLoading(true);
      setError(null);

      const adminToken = getAdminToken();
      if (!adminToken) {
        setError("No admin token found. Please login again.");
        setLoading(false);
        return;
      }

      const base = getApiBase();
      const response = await fetch(`${base}/api/complaints`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch complaints (${response.status}): ${errorText}`);
      }

      const complaintsData = await response.json();
      const complaints = complaintsData.complaints || [];

      const open       = complaints.filter((c) => c.status === "open");
      const inProgress = complaints.filter((c) => c.status === "in-progress");
      const resolved   = complaints.filter((c) => c.status === "resolved");
      const rejected   = complaints.filter((c) => c.status === "rejected");

      setOpenComplaints(open);
      setInProgressComplaints(inProgress);
      setResolvedComplaints(resolved);
      setRejectedComplaints(rejected);
      setCounts({
        open: open.length,
        inProgress: inProgress.length,
        resolved: resolved.length,
        rejected: rejected.length,
      });
    } catch (err) {
      setError("Failed to load complaints: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateComplaint = async (complaintId, status, notes) => {
    try {
      const adminToken = getAdminToken();
      if (!adminToken) throw new Error("No admin token found. Please login again.");

      const base = getApiBase();
      const response = await fetch(`${base}/api/complaints/${complaintId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ status, adminNotes: notes || "" }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.details || errorText;
        } catch (_) {}
        throw new Error(`Failed to update (${response.status}): ${errorMessage}`);
      }

      await fetchAllComplaints();
      setShowUpdateModal(false);
      setSelectedComplaint(null);
      setUpdateStatus("");
      setAdminNotes("");
      alert("✅ Complaint updated successfully!");
    } catch (err) {
      alert("❌ Failed to update complaint: " + err.message);
    }
  };

  /**
   * Downloads the log file attached to a complaint.
   *
   * Strategy:
   *  1. If the complaint has a `logFileUrl`, fetch the file via the admin-
   *     authenticated proxy endpoint so we never expose the raw storage URL
   *     to unauthenticated users, then trigger a browser download.
   *  2. Fall back to opening the URL in a new tab if the fetch/blob approach
   *     fails (e.g. CORS on the storage bucket).
   */
  const handleDownloadLog = async (complaint) => {
    const { complaintId, logFileUrl, logFileName } = complaint;

    if (!logFileUrl) return;

    // Mark this complaint as "downloading"
    setDownloadingLogs((prev) => new Set(prev).add(complaintId));

    try {
      const adminToken = getAdminToken();
      const base = getApiBase();

      // Fetch through our own backend so the Authorization header is sent.
      // The backend proxies the file from cloud storage.
      const response = await fetch(
        `${base}/api/complaints/${complaintId}/log`,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );

      if (!response.ok) {
        // Fall back: just open in new tab
        window.open(logFileUrl, "_blank", "noopener,noreferrer");
        return;
      }

      // Turn the response into a Blob and trigger a download
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      // Prefer the stored original filename; fall back to a sensible default
      anchor.download = logFileName || `${complaintId}_log.txt`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      // Release the object URL after the download starts
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
    } catch (_) {
      // Last-resort fallback: open raw URL in new tab
      window.open(logFileUrl, "_blank", "noopener,noreferrer");
    } finally {
      setDownloadingLogs((prev) => {
        const next = new Set(prev);
        next.delete(complaintId);
        return next;
      });
    }
  };

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";

  try {
    // Firestore Timestamp (REST)
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }

    // Firestore Timestamp (Admin SDK / _seconds)
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000).toLocaleString();
    }

    // Native Date or ISO string
    const date = new Date(timestamp);

    if (isNaN(date.getTime())) {
      return "N/A"; // prevents "Invalid Date"
    }

    return date.toLocaleString();
  } catch (err) {
    return "N/A";
  }
};

  const openDetailsModal = (complaint) => {
    setSelectedComplaint(complaint);
    setShowDetailsModal(true);
  };

  const openUpdateModal = (complaint) => {
    setSelectedComplaint(complaint);
    setUpdateStatus(complaint.status);
    setAdminNotes(complaint.adminNotes || "");
    setShowUpdateModal(true);
  };

  const confirmUpdate = () => {
    if (selectedComplaint && updateStatus) {
      handleUpdateComplaint(selectedComplaint.complaintId, updateStatus, adminNotes);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "open":        return "warning";
      case "in-progress": return "info";
      case "resolved":    return "success";
      case "rejected":    return "danger";
      default:            return "secondary";
    }
  };

  const getComplaintTypeIcon = (type) => {
    switch (type) {
      case "bug":     return "🐛";
      case "feature": return "💡";
      case "vendor":  return "🏪";
      case "payment": return "💳";
      case "other":   return "📝";
      default:        return "❓";
    }
  };

  const filterComplaints = (complaints) =>
    complaints.filter((complaint) => {
      const matchesSearch =
        searchTerm === "" ||
        complaint.complaintId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        complaint.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        complaint.raisedBy?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        complaint.raisedBy?.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType     = filterType === "all"     || complaint.type     === filterType;
      const matchesPlatform = filterPlatform === "all" || complaint.platform === filterPlatform;

      return matchesSearch && matchesType && matchesPlatform;
    });

  // ─── Download Log Button ────────────────────────────────────────────────────
  const DownloadLogButton = ({ complaint, size = "sm" }) => {
    const hasLog     = !!complaint.logFileUrl;
    const isLoading  = downloadingLogs.has(complaint.complaintId);

    if (!hasLog) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>No log file attached</Tooltip>}
        >
          {/* Wrapping span lets Tooltip work on a disabled button */}
          <span className="d-inline-block">
            <Button variant="outline-secondary" size={size} disabled style={{ pointerEvents: "none" }}>
              <FaFile className="me-1" />
              Log
            </Button>
          </span>
        </OverlayTrigger>
      );
    }

    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            {isLoading
              ? "Downloading…"
              : `Download ${complaint.logFileName || "log file"}`}
          </Tooltip>
        }
      >
        <Button
          variant="outline-warning"
          size={size}
          onClick={() => handleDownloadLog(complaint)}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              Downloading…
            </>
          ) : (
            <>
              <FaDownload className="me-1" />
              Log
            </>
          )}
        </Button>
      </OverlayTrigger>
    );
  };

  // ─── Complaints Table ───────────────────────────────────────────────────────
  const renderComplaintsTable = (complaints, showActions = true) => {
    const filtered = filterComplaints(complaints);

    if (filtered.length === 0) {
      return (
        <div className="text-center py-5">
          <FaExclamationTriangle size={48} className="text-muted mb-3" />
          <h5 className="text-muted">No complaints found</h5>
          <p className="text-muted">
            {searchTerm || filterType !== "all" || filterPlatform !== "all"
              ? "Try adjusting your filters"
              : activeTab === "open"
              ? "All complaints are being handled."
              : activeTab === "inProgress"
              ? "No complaints are currently in progress."
              : activeTab === "resolved"
              ? "No complaints have been resolved yet."
              : "No complaints have been rejected yet."}
          </p>
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <Table hover className="align-middle">
          <thead className="table-light">
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>User/Vendor</th>
              <th>Description</th>
              <th>Platform</th>
              <th>Screen</th>
              <th>Status</th>
              <th>Submitted</th>
              {showActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((complaint) => (
              <tr key={complaint.complaintId}>
                <td>
                  <div className="small fw-bold text-primary">
                    {complaint.complaintId}
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center">
                    <span className="me-2" style={{ fontSize: "1.2em" }}>
                      {getComplaintTypeIcon(complaint.type)}
                    </span>
                    <Badge bg="secondary">{complaint.type}</Badge>
                  </div>
                </td>
                <td>
                  <div className="fw-semibold">
                    {complaint.raisedBy?.name || "N/A"}
                  </div>
                  <div className="small text-muted">
                    <Badge
                      bg={complaint.raisedBy?.role === "vendor" ? "info" : "primary"}
                      className="me-1"
                    >
                      {complaint.raisedBy?.role || "N/A"}
                    </Badge>
                    {complaint.raisedBy?.phone || "N/A"}
                  </div>
                </td>
                <td>
                  <div className="text-truncate" style={{ maxWidth: "250px" }}>
                    {complaint.description}
                  </div>
                </td>
                <td>
                  <Badge
                    bg={
                      complaint.platform === "android"
                        ? "success"
                        : complaint.platform === "ios"
                        ? "dark"
                        : "secondary"
                    }
                  >
                    {complaint.platform}
                  </Badge>
                  <div className="small text-muted">v{complaint.appVersion}</div>
                </td>
                <td>
                  <div className="small text-muted">{complaint.screen}</div>
                </td>
                <td>
                  <Badge bg={getStatusBadgeVariant(complaint.status)}>
                    {complaint.status}
                  </Badge>
                </td>
                <td>
                  <div className="small">{formatDate(complaint.createdAt)}</div>
                </td>

                {showActions && (
                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => openDetailsModal(complaint)}
                      >
                        <FaEye className="me-1" />
                        View
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openUpdateModal(complaint)}
                      >
                        <FaCheck className="me-1" />
                        Update
                      </Button>
                      {/* ── Download Log ── */}
                      <DownloadLogButton complaint={complaint} size="sm" />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // ─── Loading / Error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
          <p className="mt-3 text-muted">Loading complaints…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-4">
        <h4>
          <FaTimes className="me-2" />
          Error
        </h4>
        <p>{error}</p>
        <Button onClick={fetchAllComplaints} variant="outline-danger">
          <FaRedo className="me-2" />
          Retry
        </Button>
      </div>
    );
  }

  // ─── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="complaints-management-container p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <FaExclamationTriangle className="me-2 text-warning" />
          Complaints Management
        </h2>
        <Button variant="outline-primary" onClick={fetchAllComplaints}>
          <FaRedo className="me-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by ID, description, name, email…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text><FaFilter /></InputGroup.Text>
                <Form.Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="all">All Types</option>
                  <option value="bug">Bug</option>
                  <option value="feature">Feature Request</option>
                  <option value="vendor">Vendor Issue</option>
                  <option value="payment">Payment</option>
                  <option value="other">Other</option>
                </Form.Select>
              </InputGroup>
            </Col>
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text><FaFilter /></InputGroup.Text>
                <Form.Select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)}>
                  <option value="all">All Platforms</option>
                  <option value="android">Android</option>
                  <option value="ios">iOS</option>
                  <option value="web">Web</option>
                </Form.Select>
              </InputGroup>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => { setSearchTerm(""); setFilterType("all"); setFilterPlatform("all"); }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="open" title={<span><FaClock className="me-2" />Open ({counts.open})</span>}>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0"><FaClock className="me-2 text-warning" />Open Complaints</h5>
            </Card.Header>
            <Card.Body>{renderComplaintsTable(openComplaints, true)}</Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="inProgress" title={<span><FaInfoCircle className="me-2" />In Progress ({counts.inProgress})</span>}>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0"><FaInfoCircle className="me-2 text-info" />In Progress Complaints</h5>
            </Card.Header>
            <Card.Body>{renderComplaintsTable(inProgressComplaints, true)}</Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="resolved" title={<span><FaCheckCircle className="me-2" />Resolved ({counts.resolved})</span>}>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0"><FaCheckCircle className="me-2 text-success" />Resolved Complaints</h5>
            </Card.Header>
            <Card.Body>{renderComplaintsTable(resolvedComplaints, true)}</Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="rejected" title={<span><FaBan className="me-2" />Rejected ({counts.rejected})</span>}>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0"><FaBan className="me-2 text-danger" />Rejected Complaints</h5>
            </Card.Header>
            <Card.Body>{renderComplaintsTable(rejectedComplaints, true)}</Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* ── Details Modal ─────────────────────────────────────────────────── */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaInfoCircle className="text-info me-2" />
            Complaint Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedComplaint && (
            <>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Complaint ID:</strong>
                    <div className="text-primary">{selectedComplaint.complaintId}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Type:</strong>
                    <div>
                      <span className="me-2" style={{ fontSize: "1.2em" }}>
                        {getComplaintTypeIcon(selectedComplaint.type)}
                      </span>
                      <Badge bg="secondary">{selectedComplaint.type}</Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div>
                      <Badge bg={getStatusBadgeVariant(selectedComplaint.status)}>
                        {selectedComplaint.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Platform:</strong>
                    <div>
                      <Badge
                        bg={
                          selectedComplaint.platform === "android"
                            ? "success"
                            : selectedComplaint.platform === "ios"
                            ? "dark"
                            : "secondary"
                        }
                      >
                        {selectedComplaint.platform}
                      </Badge>
                      <span className="ms-2 text-muted">v{selectedComplaint.appVersion}</span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Screen:</strong>
                    <div className="text-muted">{selectedComplaint.screen}</div>
                  </div>

                  {/* ── Log File row inside details modal ── */}
                  <div className="mb-3">
                    <strong>Log File:</strong>
                    <div className="mt-1">
                      {selectedComplaint.logFileUrl ? (
                        <DownloadLogButton complaint={selectedComplaint} size="sm" />
                      ) : (
                        <span className="text-muted small">No log file attached</span>
                      )}
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="mb-3">
                    <strong>Raised By:</strong>
                    <div>
                      <div className="fw-semibold">{selectedComplaint.raisedBy?.name || "N/A"}</div>
                      <div className="small text-muted">
                        <Badge
                          bg={selectedComplaint.raisedBy?.role === "vendor" ? "info" : "primary"}
                          className="me-1"
                        >
                          {selectedComplaint.raisedBy?.role || "N/A"}
                        </Badge>
                      </div>
                      <div className="small">📧 {selectedComplaint.raisedBy?.email || "N/A"}</div>
                      <div className="small">📱 {selectedComplaint.raisedBy?.phone || "N/A"}</div>
                      <div className="small">🆔 {selectedComplaint.raisedBy?.uid  || "N/A"}</div>
                    </div>
                  </div>
                  {selectedComplaint.pincode && (
                    <div className="mb-3">
                      <strong>Pincode:</strong>
                      <div className="text-muted">{selectedComplaint.pincode}</div>
                    </div>
                  )}
                  {selectedComplaint.societyName && (
                    <div className="mb-3">
                      <strong>Society:</strong>
                      <div className="text-muted">{selectedComplaint.societyName}</div>
                    </div>
                  )}
                  <div className="mb-3">
                    <strong>Submitted:</strong>
                    <div className="text-muted">{formatDate(selectedComplaint.createdAt)}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Last Updated:</strong>
                    <div className="text-muted">{formatDate(selectedComplaint.updatedAt)}</div>
                  </div>
                </Col>
              </Row>

              <div className="mb-3">
                <strong>Description:</strong>
                <div className="p-3 bg-light rounded mt-2">{selectedComplaint.description}</div>
              </div>

              {selectedComplaint.adminNotes && (
                <div className="mb-3">
                  <strong>Admin Notes:</strong>
                  <div className="p-3 bg-warning bg-opacity-10 rounded mt-2">
                    {selectedComplaint.adminNotes}
                  </div>
                </div>
              )}

              {selectedComplaint.assignedTo && (
                <div className="mb-3">
                  <strong>Assigned To:</strong>
                  <div className="text-muted">{selectedComplaint.assignedTo}</div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => { setShowDetailsModal(false); openUpdateModal(selectedComplaint); }}
          >
            Update Status
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Update Status Modal ───────────────────────────────────────────── */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaCheck className="text-success me-2" />
            Update Complaint Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedComplaint && (
            <>
              <div className="mb-4 p-3 bg-light rounded">
                <Row>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Complaint ID:</strong>
                      <div className="text-primary">{selectedComplaint.complaintId}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Raised By:</strong>
                      <div>{selectedComplaint.raisedBy?.name || "N/A"}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="mb-2">
                      <strong>Description:</strong>
                      <div className="text-muted">
                        {selectedComplaint.description.substring(0, 100)}…
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <Form.Group className="mb-3">
                <Form.Label><strong>Update Status *</strong></Form.Label>
                <Form.Select
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                  required
                >
                  <option value="">Select Status</option>
                  <option value="open">🕐 Open</option>
                  <option value="in-progress">⚙️ In Progress</option>
                  <option value="resolved">✅ Resolved</option>
                  <option value="rejected">❌ Rejected</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Admin Notes</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this complaint, actions taken, or reasons for status change…"
                />
                <Form.Text className="text-muted">
                  Optional: Add any internal notes or comments about this complaint
                </Form.Text>
              </Form.Group>

              <div className="alert alert-info">
                <FaInfoCircle className="me-2" />
                <strong>Note:</strong> The user/vendor will be able to see the status change.
                Make sure to add relevant notes if needed.
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={confirmUpdate} disabled={!updateStatus}>
            <FaCheck className="me-1" />
            Update Complaint
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .complaints-management-container {
          background-color: #f8f9fa;
          min-height: 100vh;
        }
        .table-responsive {
          border-radius: 0.375rem;
        }
        .text-truncate {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
    </div>
  );
};

export default ComplaintsManagement;